/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Button, Milestones, Sidebar, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { MilestoneStatuses } from '@insurancemenu/imenu-components-lib/dist/types';
import { useLazyQuery, useQuery } from '@apollo/client';
import { generatePath, useParams } from 'react-router-dom';
import {
  ChecklistButtonStateText,
  ChecklistCard,
  ChecklistOption,
  EnrollmentPeriod,
  EnrollmentStage,
} from 'types/enrollmentService';
import { GET_ENROLLMENT_PERIOD_BY_ID } from 'api/queries/enrollmentService';
import { Group } from 'types/groupService';
import { GROUP_BY_ID } from 'api/queries/groupService';
import { CARD_DATA } from 'global/checklists-helpers/ChecklistHelper';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { MilestoneItemType } from '@insurancemenu/imenu-components-lib/dist/types/Milestones';
import { toJS } from 'mobx';
import { employerRights, zywaveUserRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import AdditionalAccess from 'components/common/group/sidebar/AdditionalAccess';
import { GET_USER_OR_PRODUCER_BY_ID } from 'api/queries/userService';
import { FullUser, Producer } from 'types/userService';
import ContactBlock from 'components/common/group/sidebar/ContactBlock';
import { bpHistory } from 'global/bpHistory';
import { RouteV2Id, ROUTES_CONFIG_V2 } from 'routes/routesConfigV2';
import { createStyles } from './styles';

function getAorName(user: FullUser | Producer, type: 'user' | 'producer'): string {
  if (type === 'user') {
    const userUser = user as FullUser;
    return `${userUser.firstName} ${userUser.middleName || ''} ${userUser.lastName}`;
  }
  const producerUser = user as Producer;
  return producerUser?.name || '';
}

const ProposalSidebar = observer((): JSX.Element => {
  // Mobx store
  const {
    userStore: { userRoles },
  } = useRootStore();
  const classes = createStyles();

  // Query parameters
  const query = useQueryParams();
  const { periodId } = useParams<{ periodId: string }>();
  const [milestones, setMilestones] = useState<MilestoneItemType[]>([]);
  const convertedRoles = userRoles.map((role) => toJS(role)).slice();
  const cartId = query.get('cartId');

  const getEnrollmentPeriodQuery = useLazyQuery<{
    getEnrollmentPeriod: EnrollmentPeriod;
  }>(GET_ENROLLMENT_PERIOD_BY_ID, {
    fetchPolicy: 'no-cache',
    variables: { periodId },
  });

  const getGroupByIdQuery = useQuery<{
    groupById: Group;
  }>(GROUP_BY_ID, {
    skip: !getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod,
    variables: { id: getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.groupId },
  });

  const [getUserByIdQuery, { data }] = useLazyQuery<{
    userByIdOrNull: FullUser;
    getProducerOrNull: Producer;
  }>(GET_USER_OR_PRODUCER_BY_ID, {
    fetchPolicy: 'no-cache',
    variables: {
      userId: getGroupByIdQuery.data?.groupById.agentOfRecord?.id,
      id: getGroupByIdQuery.data?.groupById.agentOfRecord?.id,
    },
  });

  const userUser = data?.userByIdOrNull as FullUser | null;
  const producerUser = data?.getProducerOrNull as Producer | null;

  const effectiveDate = getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.effectiveDate;
  const browsingCriteria =
    cartId &&
    getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.proposals.find((proposal) => proposal.cartId === cartId)
      ?.productLines[0].browsingCriteria;

  // React effects
  useEffect(() => {
    void getEnrollmentPeriodQuery[0]();
  }, [getEnrollmentPeriodQuery[0]]);

  useEffect(() => {
    if (getGroupByIdQuery.data?.groupById.agentOfRecord?.id && employerRights(convertedRoles)) {
      void getUserByIdQuery();
    }
  }, [getUserByIdQuery, getGroupByIdQuery.data?.groupById.agentOfRecord?.id]);

  useEffect(() => {
    if (getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod?.checklist) {
      const checklistOptions = getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.checklist?.checklistOptions;
      const milestoneStatuses = checklistOptions.map((option) => ({
        label: CARD_DATA[option.card].title,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        status: calculateMilestoneStatus(option),
      }));
      setMilestones(milestoneStatuses);
    }
  }, [getEnrollmentPeriodQuery[1]]);

  function calculateMilestoneStatus(option: ChecklistOption<ChecklistCard>): MilestoneStatuses {
    switch (option.buttonState) {
      case ChecklistButtonStateText.VIEW_EDIT:
      case ChecklistButtonStateText.VIEW:
        return MilestoneStatuses.COMPLETED;
      case ChecklistButtonStateText.UNAVAILABLE:
        return MilestoneStatuses.INACTIVE;
      case ChecklistButtonStateText.CONTINUE:
        return MilestoneStatuses.ACTIVE;
      default:
        return MilestoneStatuses.INACTIVE;
    }
  }

  const onRedirectClick = (): void => {
    const groupId = getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.groupId;
    if (!groupId) return;

    if (employerRights(convertedRoles)) {
      bpHistory.push(generatePath(ROUTES_CONFIG_V2[RouteV2Id.EMPLOYER_PROFILE].path, { groupId }));
      return;
    }
    if (zywaveUserRights(convertedRoles)) {
      bpHistory.push(`/zywave-group/${groupId}`);
      return;
    }
    bpHistory.push(`/group/${groupId}`);
  };

  const aorName = getAorName((userUser || producerUser)!, userUser ? 'user' : 'producer');

  return (
    <Sidebar className={classes.groupSidebar}>
      <section className={classes.groupInfoSection}>
        <h2 className={classes.groupTitle}>{getGroupByIdQuery.data?.groupById.name}</h2>
        {getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.groupId && (
          <Button type="text" className={classes.viewProfileLink} size="medium" onClick={onRedirectClick}>
            (View Group Profile)
          </Button>
        )}
        <h3 className={classes.effectiveDateTitle}>Effective Date</h3>
        <p className={classes.infoText}>{effectiveDate && dateFormatters.normDateToShortAmericanDate(effectiveDate)}</p>
        {cartId && (
          <>
            <h3 className={classes.addressTitle}>Location</h3>
            <p className={classes.infoText}>
              {browsingCriteria && `${browsingCriteria.county}, ${browsingCriteria.state} ${browsingCriteria.zipCode}`}
            </p>
          </>
        )}
      </section>
      {userUser || producerUser ? (
        <ContactBlock
          blockTitle="Contact Information"
          info={{
            name: aorName,
            phone:
              userUser?.telephone ||
              producerUser?.sponsorSpecificData.phone ||
              producerUser?.sponsorSpecificData.telephone ||
              '',
            email: userUser?.email || producerUser?.sponsorSpecificData?.email || '',
          }}
        />
      ) : null}
      <section className={classes.manageEmployersSection}>
        <AdditionalAccess
          groupId={getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.groupId || ''}
          editableType="BUTTON"
          className={classes.additionalAccess}
        />
      </section>
      <section className={classes.milestones}>
        {milestones && getEnrollmentPeriodQuery[1].data?.getEnrollmentPeriod.stage !== EnrollmentStage.SUBMITTED && (
          <Milestones milestones={milestones} allConnectingLinesWidth="4rem" />
        )}
      </section>
    </Sidebar>
  );
});

export default ProposalSidebar;
